// No live updates by design — refetches happen on mutation success via the
// query-key invalidation in features/warehouse/useItems.js.

import React, { Suspense, useState, useMemo } from 'react';
import { Container, Row, Col, Form, Card, Spinner, Alert, Badge } from 'react-bootstrap';

import { useBuildings } from '../../features/warehouse/useBuildings.js';
import { useOccupancy } from '../../features/warehouse/useOccupancy.js';
import ItemDetailDrawer from '../../components/warehouse/ItemDetailDrawer.js';
import UnplacedItemsPanel from '../../components/warehouse/UnplacedItemsPanel.js';
import WarehouseStockTable from '../../components/warehouse/WarehouseStockTable.js';

// 3D building view (three.js + drei). Lazy-loaded to keep the route bundle lean.
const Building3D = React.lazy(() => import('../../components/warehouse/Building3D.js'));

export default function WarehouseMap() {
  const { data: buildings, isLoading: loadingBuildings, error: buildingsError } = useBuildings();
  const [selectedBuildingId, setSelectedBuildingId] = useState(null);
  const [searchSerial, setSearchSerial] = useState('');
  const [drawerSlotCode, setDrawerSlotCode] = useState(null);
  const [drawerEntry, setDrawerEntry] = useState(null);

  const buildingId = selectedBuildingId || buildings?.[0]?._id || null;

  const { data: occupancyData, isLoading: loadingOccupancy, error: occupancyError } =
    useOccupancy(buildingId);

  // Inject the building code into the layout so slot codes can be rebuilt.
  const layoutWithCode = useMemo(() => {
    if (!occupancyData?.building?.layout) return null;
    return { ...occupancyData.building.layout, _buildingCode: occupancyData.building.code };
  }, [occupancyData]);

  const counts = useMemo(() => {
    if (!occupancyData?.occupancy) return null;
    const c = { empty: 0, occupied: 0, reserved: 0, blocked: 0, total: 0 };
    for (const v of Object.values(occupancyData.occupancy)) {
      c.total++;
      if (v.status === 'blocked') c.blocked++;
      else if (v.status === 'empty') c.empty++;
      else if (v.item?.status === 'reserved') c.reserved++;
      else c.occupied++;
    }
    return c;
  }, [occupancyData]);

  const handleSlotClick = (code, entry) => {
    setDrawerSlotCode(code);
    setDrawerEntry(entry);
  };

  if (loadingBuildings) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  if (buildingsError) {
    return (
      <Container className="py-5">
        <Alert variant="danger">Failed to load buildings: {buildingsError.message}</Alert>
      </Container>
    );
  }

  if (!buildings?.length) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          No buildings found. Run <code>npm run seed</code> in the warehouse-module backend.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col md={3}>
          <Form.Label>Building</Form.Label>
          <Form.Select
            value={buildingId || ''}
            onChange={e => setSelectedBuildingId(e.target.value)}
          >
            {buildings.map(b => (
              <option key={b._id} value={b._id}>
                {b.code} — {b.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={4}>
          <Form.Label>Search by serial</Form.Label>
          <Form.Control
            type="text"
            placeholder="e.g. D-0001"
            value={searchSerial}
            onChange={e => setSearchSerial(e.target.value.trim())}
          />
        </Col>
        <Col md={5} className="d-flex align-items-end justify-content-end gap-3">
          {counts && (
            <div className="d-flex gap-2">
              <Badge bg="secondary">{counts.empty} empty</Badge>
              <Badge bg="success">{counts.occupied} occupied</Badge>
              <Badge bg="primary">{counts.reserved} reserved</Badge>
              <Badge bg="danger">{counts.blocked} blocked</Badge>
              <Badge bg="dark">{counts.total} total</Badge>
            </div>
          )}
        </Col>
      </Row>

      <Row>
        <Col md={9}>
          <Card>
            <Card.Body>
              {loadingOccupancy && (
                <div className="text-center py-5">
                  <Spinner animation="border" size="sm" /> Loading slots…
                </div>
              )}
              {occupancyError && (
                <Alert variant="danger">Failed to load occupancy: {occupancyError.message}</Alert>
              )}
              {!loadingOccupancy && layoutWithCode && (
                <Suspense
                  fallback={
                    <div className="text-center py-5">
                      <Spinner animation="border" size="sm" /> Loading 3D view…
                    </div>
                  }
                >
                  <Building3D
                    layout={layoutWithCode}
                    occupancy={occupancyData.occupancy}
                    onSlotClick={handleSlotClick}
                    highlightSerial={searchSerial || null}
                  />
                </Suspense>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <UnplacedItemsPanel buildingId={buildingId} />
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <WarehouseStockTable
            buildingId={buildingId}
            title={`Stock in ${occupancyData?.building?.code || 'this depot'}`}
          />
        </Col>
      </Row>

      <ItemDetailDrawer
        show={!!drawerSlotCode}
        onHide={() => setDrawerSlotCode(null)}
        slotCode={drawerSlotCode}
        entry={drawerEntry}
        buildingId={buildingId}
      />
    </Container>
  );
}
